import React, {useMemo} from 'react';

import {
  Autocomplete,
  MenuItem,
  SvgIcon,
} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {ReactComponent as ChevronDown} from 'assets/svg/chevron-down-thin.svg';
import {ZmTextField} from 'Components/ZmInputs';
import {COUNTRY_DATA} from 'Utils/phoneCountryCodes';

/**
 * Country Code Input Component
 * @param {Object} props
 * @return {ReactElement}
 */
export default function CountryInput(props) {
  const {
    error,
    hideLabel,
    id,
    onChange,
    value,
  } = props;
  const {t} = useTranslation();

  // Get the option item with value corresponding to the value string
  const autocompleteValue = useMemo(() => {
    if (value) {
      return COUNTRY_DATA.find((opt) => opt.phone === value) || null;
    }
    return null;
  }, [value]);

  const filterOptions = (options, {inputValue}) => {
    return options.filter((option) => {
      // eslint-disable-next-line max-len
      return option.label.toLowerCase().includes(inputValue.toLowerCase()) || option.phone.toLowerCase().includes(inputValue.toLowerCase());
    });
  };

  return (
    <Autocomplete
      componentsProps={{
        paper: {
          sx: {
            width: 'fit-content',
          },
        },
      }}
      id={`ac-${id}`}
      disableClearable={true}
      options={COUNTRY_DATA}
      value={autocompleteValue}
      autoHighlight
      filterOptions={filterOptions}
      clearIcon={null}
      getOptionLabel={(option) => `${option.phone.split('-')[0]}`}
      popupIcon={<SvgIcon fontSize="inherit" component={ChevronDown} inheritViewBox />}
      renderOption={(props, option) => (
        <MenuItem
          {...props}
          key={option.label}
        >
          {option.label} ({option.phone})
        </MenuItem>
      )}
      onChange={(e, value, reason) => onChange(value.phone)}
      renderInput={(params) => (
        <ZmTextField
          role="listbox"
          margin="dense"
          fullWidth
          variant="outlined"
          aria-required
          label={hideLabel? undefined: t('booking.phoneCountryCode')}
          id={id}
          {...params}
          placeholder={'Country/Region code'}
          sx={{
            '.MuiInputBase-root.MuiOutlinedInput-root': {
              alignContent: 'center',
              paddingRight: '30px',
            },
            '.MuiAutocomplete-popupIndicator': {
              fontSize: '1rem',
              marginTop: '4px',
            },
            '& label': {
              textWrap: 'nowrap',
            },
          }}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'tel-country-code',
            minLength: 2,
            maxLength: 6,
            style: {
              textAlign: 'right',
            },
          }}
          aria-live="polite"
          error={!!error?.message}
          helperText={error?.message}
        />
      )}
    />
  );
}

